import { Particle } from '../ParticleLayer';

// GLOBAL SPRITE CACHE FOR SHOCK EFFECT
const shockSpriteCache = new Map<string, HTMLCanvasElement>();

function getShockGlowSprite(size: number): HTMLCanvasElement {
    const qSize = Math.max(1, Math.round(size));
    const key = `glow-${qSize}`;
    let sprite = shockSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        const diameter = qSize * 2;
        sprite.width = diameter + 4;
        sprite.height = diameter + 4;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = sprite.width / 2;
            const centerY = sprite.height / 2;
            const grad = sCtx.createRadialGradient(centerX, centerY, 0, centerX, centerY, qSize);
            grad.addColorStop(0, 'rgba(255, 255, 100, 1)');
            grad.addColorStop(0.4, 'rgba(255, 200, 50, 0.6)');
            grad.addColorStop(1, 'rgba(255, 200, 50, 0)');
            sCtx.fillStyle = grad;
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, qSize, 0, Math.PI * 2);
            sCtx.fill();
        }
        shockSpriteCache.set(key, sprite);
    }
    return sprite;
}

function getShockSparkSprite(color: string, size: number): HTMLCanvasElement {
    const qSize = Math.max(1, Math.round(size));
    const key = `spark-${color}-${qSize}`;
    let sprite = shockSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        const blurAmount = 10;
        const diameter = qSize * 2;
        // Need extra padding for shadowBlur
        sprite.width = diameter + blurAmount * 2;
        sprite.height = diameter + blurAmount * 2;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = sprite.width / 2;
            const centerY = sprite.height / 2;
            sCtx.fillStyle = color;
            sCtx.shadowBlur = blurAmount;
            sCtx.shadowColor = color;
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, qSize, 0, Math.PI * 2);
            sCtx.fill();
        }
        shockSpriteCache.set(key, sprite);
    }
    return sprite;
}

export type ShockEffectData = {
    targetId: string;
    sourceX: number;
    sourceY: number;
    targetX: number;
    targetY: number;

    // Phase control
    phase: 'charge' | 'strike' | 'impact';

    // Charge-up particles
    chargeParticles: {
        angle: number;
        dist: number;
        speed: number;
        color: string;
    }[];

    // Main Bolt Paths (for particles) - Now an array of paths
    boltPaths: { x: number, y: number }[][];

    // Particles for sparks/impact/trail
    sparks: {
        x: number;
        y: number;
        vx: number;
        vy: number;
        color: string;
        life: number;
        maxLife: number;
        size: number;
        type: 'spark' | 'glow'; // 'glow' for the yellow trace particles
    }[];

    // Expanding ring at impact
    impactRing: {
        radius: number;
        alpha: number;
    } | null;

    // Random background lightnings
    randomBolts: {
        sx: number;
        sy: number;
        ex: number;
        ey: number;
        life: number;
        maxLife: number;
        color: string;
        seedOffset: number; // To make each bolt look different
    }[];
};

export type ShockEffect = {
    kind: 'shock';
    t: number;
    data: ShockEffectData;
};

export function createShockEffect(
    sourceX: number,
    sourceY: number,
    targetX: number,
    targetY: number,
    targetId: string
): ShockEffect {
    return {
        kind: 'shock',
        t: 0,
        data: {
            targetId,
            sourceX,
            sourceY,
            targetX,
            targetY,
            phase: 'charge',
            chargeParticles: Array(30).fill(0).map(() => ({
                angle: Math.random() * Math.PI * 2,
                dist: 40 + Math.random() * 60,
                speed: 4 + Math.random() * 5,
                color: Math.random() > 0.5 ? '#00ffff' : '#ffffff' // Cyan/White
            })),
            boltPaths: [],
            sparks: [],
            impactRing: null,
            randomBolts: []
        },
    };
}

// --- FBM Noise Helpers (Ported/Adapted from Animation.txt Shader) ---

function fract(x: number) {
    return x - Math.floor(x);
}

function hash(p: number) {
    p = fract(p * 0.1031);
    p *= p + 33.33;
    p *= p + p;
    return fract(p);
}

function noise(x: number) {
    const i = Math.floor(x);
    const f = fract(x);
    const u = f * f * (3.0 - 2.0 * f);
    return (1 - u) * hash(i) + u * hash(i + 1);
}

function fbm(x: number, octaves: number = 5) {
    let v = 0;
    let a = 0.5;
    let shift = x;
    for (let i = 0; i < octaves; i++) {
        v += a * noise(shift);
        shift = shift * 2.0;
        a *= 0.5;
    }
    return v; // Returns roughly 0.0 to 1.0
}

// Generate lightning points using FBM noise for displacement
function generateFbmLightning(
    x1: number, y1: number,
    x2: number, y2: number,
    time: number,
    seedOffset: number = 0,
    amplitude: number = 20
): { x: number, y: number }[] {
    const points: { x: number, y: number }[] = [];
    const steps = 60; // More segments for smoother look
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    const nx = -dy / len; // Normal vector
    const ny = dx / len;

    for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        // Base position
        let px = x1 + dx * t;
        let py = y1 + dy * t;

        // Noise displacement
        // We use 't' (position along line) and 'time' (animation)
        // seedOffset ensures different bolts look different
        const noiseVal = fbm(t * 12 + time * 3 + seedOffset) - 0.5;

        // Envelope to keep ends fixed
        const envelope = Math.sin(t * Math.PI);

        const displacement = noiseVal * amplitude * envelope;

        px += nx * displacement;
        py += ny * displacement;

        points.push({ x: px, y: py });
    }
    return points;
}

// Helper to draw a lightning bolt with the "Electric" look
function drawLightning(
    ctx: CanvasRenderingContext2D,
    points: { x: number, y: number }[],
    hue: number, // HSV Hue
    width: number,
    alpha: number
) {
    if (points.length < 2) return;

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalCompositeOperation = 'lighter'; // Key for "electric" look

    // Convert Hue to CSS color strings
    const baseColor = `hsla(${hue}, 80%, 60%, ${alpha})`;
    const glowColor = `hsla(${hue}, 90%, 70%, ${alpha * 0.6})`;
    const coreColor = `hsla(${hue}, 20%, 95%, ${alpha})`; // Almost white

    // 1. Outer Glow (Wide, Soft)
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);

    ctx.strokeStyle = glowColor;
    ctx.lineWidth = width * 8;
    ctx.globalAlpha = alpha * 0.3;
    ctx.shadowBlur = 30;
    ctx.shadowColor = glowColor;
    ctx.stroke();

    // 2. Inner Glow (Brighter)
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);

    ctx.strokeStyle = baseColor;
    ctx.lineWidth = width * 3;
    ctx.globalAlpha = alpha * 0.6;
    ctx.shadowBlur = 15;
    ctx.stroke();

    // 3. Core (White Hot)
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);

    ctx.strokeStyle = coreColor;
    ctx.lineWidth = width * 1.2;
    ctx.globalAlpha = alpha;
    ctx.shadowBlur = 8;
    ctx.shadowColor = '#ffffff';
    ctx.stroke();

    ctx.restore();
}

export function renderShockEffect(
    effect: ShockEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean {
    const CHARGE_TIME = 800;
    const STRIKE_TIME = 1500;
    const TOTAL_TIME = 3500;

    const df = (effect as any)._df ?? 1;
    const { data, t } = effect;

    if (t > TOTAL_TIME) return false;

    // --- Phase Logic ---
    if (t < CHARGE_TIME) {
        data.phase = 'charge';
    } else if (t < CHARGE_TIME + STRIKE_TIME) {
        data.phase = 'strike';
    } else {
        data.phase = 'impact';
    }

    ctx.save();

    // --- 1. Charge Up Phase ---
    if (data.phase === 'charge') {
        const progress = t / CHARGE_TIME;
        const centerAlpha = Math.min(1, progress * 2);

        // Glowing center
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = centerAlpha;
        ctx.shadowBlur = 40 * progress;
        ctx.shadowColor = '#00ffff';
        ctx.fillStyle = '#e0ffff';
        ctx.beginPath();
        ctx.arc(data.sourceX, data.sourceY, 5 + progress * 20, 0, Math.PI * 2);
        ctx.fill();

        // Inward sucking particles
        data.chargeParticles.forEach(p => {
            p.dist -= p.speed * df;
            if (p.dist < 0) p.dist = 60 + Math.random() * 40;

            const px = data.sourceX + Math.cos(p.angle) * p.dist;
            const py = data.sourceY + Math.sin(p.angle) * p.dist;

            p.angle += 0.2 * df;

            ctx.globalAlpha = (p.dist / 100) * centerAlpha;
            ctx.fillStyle = p.color;
            ctx.beginPath();
            ctx.arc(px, py, 3, 0, Math.PI * 2);
            ctx.fill();
        });

        // Occasional small arcs (FBM style)
        if (Math.random() < 0.5 * progress) {
            const angle = Math.random() * Math.PI * 2;
            const dist = 40 + Math.random() * 40;
            const tx = data.sourceX + Math.cos(angle) * dist;
            const ty = data.sourceY + Math.sin(angle) * dist;

            // Use FBM for these small arcs too
            const pts = generateFbmLightning(data.sourceX, data.sourceY, tx, ty, t * 0.01, Math.random() * 100, 8);
            drawLightning(ctx, pts, 190, 1, 0.7 * progress); // Cyan hue
        }
    }

    // --- Random Board-Wide Lightnings ---
    if (t > 200 && t < TOTAL_TIME - 500) {
        if (Math.random() < 0.08 * df) {
            const w = ctx.canvas.width;
            const h = ctx.canvas.height;
            const sx = Math.random() * w;
            const sy = Math.random() * (h * 0.3);
            const ex = Math.random() * w;
            const ey = Math.random() * h;

            data.randomBolts.push({
                sx, sy, ex, ey,
                life: 300,
                maxLife: 300,
                color: Math.random() > 0.5 ? '#00ffff' : '#ff00ff',
                seedOffset: Math.random() * 1000
            });

            // Splash at impact
            for (let i = 0; i < 8; i++) {
                const angle = Math.random() * Math.PI * 2;
                const speed = 4 + Math.random() * 6;
                data.sparks.push({
                    x: ex,
                    y: ey,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed - 2,
                    color: '#ffffaa', // Pale yellow
                    life: 400 + Math.random() * 300,
                    maxLife: 700,
                    size: 2 + Math.random() * 3,
                    type: 'spark'
                });
            }
        }
    }

    // Render Random Bolts
    for (let i = data.randomBolts.length - 1; i >= 0; i--) {
        const bolt = data.randomBolts[i];
        bolt.life -= 16 * df;
        if (bolt.life <= 0) {
            data.randomBolts[i] = data.randomBolts[data.randomBolts.length - 1];
            data.randomBolts.pop();
            continue;
        }
        const alpha = bolt.life / bolt.maxLife;
        // Animate the random bolt slightly
        const pts = generateFbmLightning(bolt.sx, bolt.sy, bolt.ex, bolt.ey, t * 0.005, bolt.seedOffset, 50);
        drawLightning(ctx, pts, 200, 1.5, alpha * 0.9);
    }


    // --- 2. Strike Phase (Palpatine Style) ---
    if (data.phase === 'strike') {
        const strikeT = t - CHARGE_TIME;

        // Initial Flash
        if (strikeT < 100) {
            ctx.globalCompositeOperation = 'lighter';
            ctx.fillStyle = '#e0ffff';
            ctx.globalAlpha = 0.4 * (1 - strikeT / 100);
            ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        }

        // Generate Multiple Main Bolts (The "Force Storm")
        data.boltPaths = []; // Reset for this frame

        const numBolts = 5; // 5 main tendrils
        for (let i = 0; i < numBolts; i++) {
            // Spread the target points around the actual target
            // Palpatine style: wide cone
            const spreadAngle = (i - (numBolts - 1) / 2) * 0.08; // Much tighter spread (was 0.3)
            const dx = data.targetX - data.sourceX;
            const dy = data.targetY - data.sourceY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const baseAngle = Math.atan2(dy, dx);

            const targetAngle = baseAngle + spreadAngle + (Math.random() - 0.5) * 0.1; // Reduced jitter

            // Target point is near the target but spread out
            const tx = data.sourceX + Math.cos(targetAngle) * dist;
            const ty = data.sourceY + Math.sin(targetAngle) * dist;

            // Jitter the end point heavily
            const jitterX = (Math.random() - 0.5) * 30;
            const jitterY = (Math.random() - 0.5) * 30;

            const pts = generateFbmLightning(
                data.sourceX, data.sourceY,
                tx + jitterX, ty + jitterY,
                t * 0.01 + i * 100, // Offset time for each bolt
                i * 500, // Different seed
                40 // High amplitude
            );
            data.boltPaths.push(pts);

            // Draw Main Bolt
            drawLightning(ctx, pts, 190 + i * 5, 2.5, 1.0); // Slight hue shift per bolt

            // Branching Bolts (Recursive-ish)
            // Spawn 2-3 branches from random points on this bolt
            const numBranches = 3;
            for (let j = 0; j < numBranches; j++) {
                const splitIdx = Math.floor(Math.random() * (pts.length - 10)) + 5;
                const splitPt = pts[splitIdx];
                if (!splitPt) continue;

                // Branch goes off in a random direction generally forward
                const branchAngle = targetAngle + (Math.random() - 0.5) * 1.0; // Tighter branching
                const branchDist = dist * 0.3; // Shorter
                const bx = splitPt.x + Math.cos(branchAngle) * branchDist;
                const by = splitPt.y + Math.sin(branchAngle) * branchDist;

                const branchPts = generateFbmLightning(
                    splitPt.x, splitPt.y,
                    bx, by,
                    t * 0.015,
                    i * 1000 + j * 100,
                    20
                );
                drawLightning(ctx, branchPts, 210, 1.5, 0.7);
            }
        }

        // Spawn Glowing Yellow Particles along the paths
        if (strikeT % 32 < 16) {
            data.boltPaths.forEach(path => {
                if (Math.random() < 0.5) return; // Not every bolt every frame
                const ptIdx = Math.floor(Math.random() * (path.length - 1));
                const pt = path[ptIdx];
                if (pt) {
                    data.sparks.push({
                        x: pt.x + (Math.random() - 0.5) * 20,
                        y: pt.y + (Math.random() - 0.5) * 20,
                        vx: (Math.random() - 0.5) * 4,
                        vy: (Math.random() - 0.5) * 4 - 1,
                        color: '#ffff00',
                        life: 500 + Math.random() * 400,
                        maxLife: 900,
                        size: 4 + Math.random() * 6,
                        type: 'glow'
                    });
                }
            });
        }

        // Impact Trigger
        if (!data.impactRing && strikeT > 0) {
            data.impactRing = { radius: 10, alpha: 1 };
            // Impact Sparks - Massive explosion of sparks
            for (let i = 0; i < 80; i++) {
                const angle = Math.random() * Math.PI * 2;
                const speed = 8 + Math.random() * 20;
                data.sparks.push({
                    x: data.targetX,
                    y: data.targetY,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed,
                    color: Math.random() > 0.5 ? '#ffffff' : '#00ffff',
                    life: 600 + Math.random() * 400,
                    maxLife: 1000,
                    size: 3 + Math.random() * 5,
                    type: 'spark'
                });
            }
        }
    }

    // --- 3. Impact & Residuals ---

    // Draw Impact Ring
    if (data.impactRing) {
        data.impactRing.radius += 12 * df;
        data.impactRing.alpha -= 0.03 * df;
        if (data.impactRing.alpha > 0) {
            ctx.save();
            ctx.globalCompositeOperation = 'lighter';
            ctx.beginPath();
            ctx.arc(data.targetX, data.targetY, data.impactRing.radius, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(0, 255, 255, ${data.impactRing.alpha})`;
            ctx.lineWidth = 10;
            ctx.shadowBlur = 40;
            ctx.shadowColor = '#00ffff';
            ctx.stroke();
            ctx.restore();
        }
    }

    // Render Particles
    for (let i = data.sparks.length - 1; i >= 0; i--) {
        const p = data.sparks[i];
        p.life -= 16 * df;
        p.x += p.vx * df;
        p.y += p.vy * df;

        if (p.life <= 0) {
            data.sparks[i] = data.sparks[data.sparks.length - 1];
            data.sparks.pop();
            continue;
        }

        if (p.type === 'glow') {
            const alpha = Math.min(0.8, p.life / 400);
            ctx.globalAlpha = alpha;
            ctx.globalCompositeOperation = 'lighter';

            const sprite = getShockGlowSprite(p.size);
            const offset = sprite.width / 2;
            ctx.drawImage(sprite, p.x - offset, p.y - offset);

        } else {
            p.vy += 0.4 * df;
            ctx.globalAlpha = p.life / p.maxLife;
            // shadowBlur removed from loop, embedded in offscreen sprite
            const sprite = getShockSparkSprite(p.color, p.size);
            const offset = sprite.width / 2;
            ctx.drawImage(sprite, p.x - offset, p.y - offset);
        }
    }

    ctx.restore();
    return true;
}
